import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { ProductFrame } from '@/components/shared/ProductFrame';
import { SignalChip } from '@/components/shared/SignalChip';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO, breadcrumbSchema, faqSchema } from '@/components/shared/SEO';
import { fadeInUp, staggerContainer, viewportOnce } from '@/lib/motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, ArrowRight } from 'lucide-react';

const solutionsData = {
  'meta-ads': {
    badge: 'Meta Ads',
    title: 'Monitor every Meta ad your competitors run',
    subtitle: 'Track Facebook and Instagram ad creatives, copy, landing pages, and campaign strategies in real time.',
    useCases: [
      { title: 'Creative analysis', desc: 'See every image, video, and carousel ad your competitors launch. Analyze what creatives are working.' },
      { title: 'Copy & CTA tracking', desc: 'Track ad copy, headlines, and calls to action. Spot patterns in messaging strategies.' },
      { title: 'Campaign timing', desc: 'Know exactly when competitors launch, pause, or scale campaigns. Never miss a move.' },
    ],
    features: ['Real-time ad detection', 'Creative archive', 'Landing page capture', 'Ad spend estimation', 'Campaign timeline', 'Multi-account monitoring'],
    stats: [{ val: '5.2M+', label: 'Meta ads tracked' }, { val: '<2min', label: 'Detection speed' }, { val: '98%', label: 'Coverage rate' }],
    faqs: [
      { q: 'How quickly are new Meta ads detected?', a: 'Most new ads are detected within 2 minutes of going live. Our systems continuously monitor the Meta Ad Library and platform signals.' },
      { q: 'Can I track competitor Instagram Story ads?', a: 'Yes. RADAR tracks all Meta ad placements including Feed, Stories, Reels, and the Audience Network.' },
      { q: 'Is Meta ads monitoring available on all plans?', a: 'Yes. Meta ads monitoring is included in all RADAR plans, from Starter to Enterprise.' },
    ],
  },
  'tiktok-ads': {
    badge: 'TikTok Ads',
    title: 'Track every TikTok ad your competitors create',
    subtitle: 'Monitor TikTok ad creatives, trends, and competitor campaign strategies across the fastest-growing ad platform.',
    useCases: [
      { title: 'Trend detection', desc: 'Spot which ad formats and creative styles competitors are testing on TikTok before they scale.' },
      { title: 'Creative library', desc: 'Build a searchable archive of competitor TikTok creatives for inspiration and analysis.' },
      { title: 'Performance signals', desc: 'Detect which ads competitors keep running longest — a strong signal of performance.' },
    ],
    features: ['TikTok Ad Library monitoring', 'Video creative archiving', 'Trend detection', 'Campaign duration tracking', 'Hashtag analysis', 'Sound & music tracking'],
    stats: [{ val: '3.1M+', label: 'TikTok ads tracked' }, { val: '500K+', label: 'Videos archived' }, { val: '24/7', label: 'Monitoring' }],
    faqs: [
      { q: 'Does RADAR track TikTok Spark Ads?', a: 'Yes. We track all TikTok ad formats including In-Feed, TopView, Spark Ads, and branded effects campaigns.' },
      { q: 'Can I download competitor TikTok creatives?', a: 'Yes. Pro and Enterprise plans allow you to download and archive competitor creatives for internal analysis.' },
      { q: 'How far back does TikTok ad history go?', a: 'Our TikTok ad archive goes back up to 12 months depending on when the competitor was added to your monitoring list.' },
    ],
  },
  'google-ads': {
    badge: 'Google Ads',
    title: 'Analyze competitor Google Ads strategies',
    subtitle: 'Monitor competitor search ads, display campaigns, and shopping ads across Google networks.',
    useCases: [
      { title: 'Keyword intelligence', desc: 'See which keywords competitors are bidding on and how their ad copy positions against yours.' },
      { title: 'Ad copy analysis', desc: 'Track competitor headlines, descriptions, and extensions across search campaigns.' },
      { title: 'Shopping ads', desc: 'Monitor competitor product listings, pricing, and promotional strategies in Google Shopping.' },
    ],
    features: ['Search ad monitoring', 'Display ad tracking', 'Shopping ads intelligence', 'Keyword overlap analysis', 'Ad extension tracking', 'Landing page capture'],
    stats: [{ val: '4.8M+', label: 'Google ads tracked' }, { val: '2M+', label: 'Keywords monitored' }, { val: '99.5%', label: 'Uptime' }],
    faqs: [
      { q: 'Which Google ad types does RADAR track?', a: 'RADAR monitors Search, Display, Shopping, and Performance Max campaigns across all Google networks.' },
      { q: 'Can I see competitor keyword bids?', a: 'We provide estimated bid ranges and impression share data to help you understand competitor spending patterns.' },
      { q: 'Is Google Ads monitoring a paid add-on?', a: 'Google Ads monitoring is included in Pro and Enterprise plans. Starter plans include basic Google ad tracking.' },
    ],
  },
  'competitor-pricing': {
    badge: 'Pricing',
    title: 'Track competitor pricing in real time',
    subtitle: 'Monitor price changes, stock levels, and discount strategies across competitor storefronts.',
    useCases: [
      { title: 'Price tracking', desc: 'Get alerted instantly when competitors change prices on products you care about.' },
      { title: 'Stock monitoring', desc: 'Know when competitors go out of stock or restock popular items.' },
      { title: 'Discount detection', desc: 'Track promotional campaigns, coupon patterns, and seasonal discount strategies.' },
    ],
    features: ['Real-time price monitoring', 'Stock level tracking', 'Discount detection', 'Historical price charts', 'Product matching', 'Price comparison reports'],
    stats: [{ val: '50K+', label: 'Products monitored' }, { val: '24/7', label: 'Price checking' }, { val: '<1min', label: 'Alert speed' }],
    faqs: [
      { q: 'How often are prices checked?', a: 'Prices are checked continuously throughout the day. Critical products can be monitored at intervals as low as every 5 minutes.' },
      { q: 'Can I set custom price alert thresholds?', a: 'Yes. You can set alerts for specific price changes (e.g., more than 10% drop) or absolute price points.' },
      { q: 'Does RADAR track historical pricing?', a: 'Yes. We maintain full price history for all monitored products, allowing you to analyze trends over time.' },
    ],
  },
};

export default function SolutionPageNew({ slug }) {
  const data = solutionsData[slug];

  if (!data) {
    return (
      <Section hero>
        <Container>
          <h1 className="text-2xl font-display font-bold text-foreground">Solution not found</h1>
          <Link to="/solutions" className="text-lime-400 hover:text-lime-300 mt-4 inline-block">Back to Solutions</Link>
        </Container>
      </Section>
    );
  }

  return (
    <>
      <SEO
        title={`${data.badge} Intelligence`}
        description={data.subtitle}
        canonical={`/solutions/${slug}`}
        structuredData={[
          breadcrumbSchema([
            { name: 'Home', href: '/' },
            { name: 'Solutions', href: '/solutions' },
            { name: data.badge, href: `/solutions/${slug}` },
          ]),
          faqSchema(data.faqs),
        ]}
      />
      {/* Hero */}
      <Section hero className="relative overflow-hidden">
        <div className="absolute inset-0 radar-glow-hero opacity-50" />
        <Container className="relative z-10">
          <AnimatedReveal>
            <div className="flex items-center gap-3 mb-4">
              <Link to="/solutions" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Solutions</Link>
              <span className="text-muted-foreground/40">/</span>
              <SignalChip variant="lime">{data.badge}</SignalChip>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-foreground mb-4 max-w-[700px]">
              {data.title}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-[560px] mb-8">{data.subtitle}</p>
            <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_30px_rgba(204,255,108,0.2)]">
              Start free trial <ArrowRight className="h-4 w-4" />
            </Link>
          </AnimatedReveal>
        </Container>
      </Section>

      {/* Use Cases */}
      <Section dense>
        <Container>
          <motion.div className="grid md:grid-cols-3 gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            {data.useCases.map((uc, i) => (
              <motion.div key={i} variants={fadeInUp}>
                <GlassPanel className="p-6 h-full">
                  <h3 className="text-base font-display font-semibold text-foreground mb-2">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                </GlassPanel>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Features + Mock */}
      <Section>
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedReveal>
              <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Features</p>
              <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-8">What you get</h2>
              <ul className="space-y-4">
                {data.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-foreground/80">
                    <Check className="h-4 w-4 text-lime-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </AnimatedReveal>
            <AnimatedReveal delay={0.2}>
              <ProductFrame title={`radar — ${data.badge.toLowerCase()}`}>
                <div className="p-6 space-y-3">
                  {data.useCases.map((uc, i) => (
                    <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-2 w-2 rounded-full bg-lime-500" />
                        <p className="text-xs font-mono text-lime-400 uppercase">{uc.title}</p>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{uc.desc.slice(0, 80)}...</p>
                    </div>
                  ))}
                </div>
              </ProductFrame>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>

      {/* Stats */}
      <Section dense className="border-y border-white/[0.04]">
        <Container>
          <motion.div className="grid grid-cols-3 gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            {data.stats.map((s, i) => (
              <motion.div key={i} variants={fadeInUp} className="text-center">
                <p className="text-3xl font-display font-bold text-foreground">{s.val}</p>
                <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section>
        <Container>
          <AnimatedReveal>
            <h2 className="text-2xl font-display font-bold text-foreground mb-8">Frequently asked questions</h2>
          </AnimatedReveal>
          <div className="max-w-2xl">
            <Accordion type="single" collapsible className="space-y-2">
              {data.faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-white/[0.06] rounded-xl px-5 data-[state=open]:border-white/10 data-[state=open]:bg-white/[0.02]">
                  <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline py-5">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section>
        <Container>
          <AnimatedReveal>
            <GlassPanel className="p-10 sm:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-lime-500/[0.03] to-transparent pointer-events-none" />
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">Start monitoring {data.badge}</h2>
                <p className="text-muted-foreground mb-8">7-day free trial. No credit card required.</p>
                <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_30px_rgba(204,255,108,0.2)]">
                  Start free trial <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </GlassPanel>
          </AnimatedReveal>
        </Container>
      </Section>
    </>
  );
}
